import { CoreProviderFieldDescriptor } from '@core/api/core';

export interface ProviderFieldGroup {
  key: string;
  label: string;
  fields: CoreProviderFieldDescriptor[];
}

export interface ProviderFieldValueChange {
  field: CoreProviderFieldDescriptor;
  value: unknown;
}

export function groupProviderFields(
  fields: CoreProviderFieldDescriptor[],
): ProviderFieldGroup[] {
  const groups = new Map<string, ProviderFieldGroup>();
  for (const field of fields) {
    const key = field.group ?? 'filters';
    const existing = groups.get(key);
    if (existing) {
      existing.fields.push(field);
      continue;
    }

    groups.set(key, {
      key,
      label: key.replace(/_/g, ' '),
      fields: [field],
    });
  }
  return Array.from(groups.values());
}

export function defaultProviderFieldValue(field: CoreProviderFieldDescriptor): unknown {
  switch (field.type) {
    case 'multiselect':
      return [];
    case 'boolean':
      return null;
    case 'number':
      return null;
    case 'date':
    case 'select':
    case 'text':
      return '';
    default: {
      const exhaustive: never = field.type;
      return exhaustive;
    }
  }
}

export function isProviderFieldValueActive(
  field: CoreProviderFieldDescriptor,
  value: unknown,
): boolean {
  switch (field.type) {
    case 'multiselect':
      return Array.isArray(value) && value.length > 0;
    case 'boolean':
      return typeof value === 'boolean';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'date':
    case 'select':
    case 'text':
      return typeof value === 'string' && value.trim() !== '';
    default: {
      const exhaustive: never = field.type;
      return exhaustive;
    }
  }
}

export function normalizeProviderFieldValue(
  field: CoreProviderFieldDescriptor,
  value: unknown,
): unknown {
  switch (field.type) {
    case 'multiselect':
      return Array.isArray(value) ? value.map(String) : [];
    case 'boolean':
      return typeof value === 'boolean' ? value : null;
    case 'number':
      return typeof value === 'number' ? value : null;
    case 'date':
    case 'select':
    case 'text':
      return typeof value === 'string' ? value : '';
    default: {
      const exhaustive: never = field.type;
      return exhaustive;
    }
  }
}

export function buildProviderFiltersPayload(
  fields: CoreProviderFieldDescriptor[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of fields) {
    const value = values[field.name];
    if (!isProviderFieldValueActive(field, value)) {
      continue;
    }

    if (typeof value === 'string') {
      payload[field.name] = value.trim();
    } else {
      payload[field.name] = value;
    }
  }
  return payload;
}
